import { Nullish, TypedGetterMap, TypedGetterSetterMap } from 'ng/src/app/shared/model/app.model';

export type DeepPartial<T> = {
    [P in keyof T]?: DeepPartial<T[P]>;
};

export type SortKeyGetter<E> = keyof E | ((val: E) => any)
export type SortOrder = 'asc' | 'desc'
export type MultiSortOrder<E> = [SortKeyGetter<E>, SortOrder]

export type MultiSortConfig<E> = MultiSortKeyConfig<E> | MultiSortCompareConfig<E>;

type MultiSortKeyConfig<E> = {
	key: SortKeyGetter<E>,
	sort?: SortOrder
}

type MultiSortCompareConfig<E> = {
	compare: (a: E, b: E) => number
}

export type EntryCollection<T extends object, K extends PropertyKey> = Array<[K, Array<T>]>;

export class Utils {
	static get isMobile(): boolean {
		return window.innerWidth < 768;
	}
	
	static capitalizeFirstLetter(string: string) {
		return string.charAt(0).toUpperCase() + string.slice(1);
	}

	static camelToTitleCase(camelCase: string) {
		return camelCase
			.replace(/([A-Z])/g, match => ` ${match}`)
			.replace(/^./, match => match.toUpperCase())
			.trim();
	}

	static isInRange(n: number, min: number, max: number) {
		return n >= min && n <= max;
	}

	static isBetween(n: number, min: number, max: number) {
		return n > min && n < max;
	}

	static oxfordCommafy(array: string[]) {
		const arrayCopy = [...array],
			  lastElement = arrayCopy.pop();
		
		if (lastElement) {
			if (arrayCopy.length > 1)
				return arrayCopy.join(', ') + ', and ' + lastElement;
			if (arrayCopy.length == 1)
				return arrayCopy[0] + ' and ' + lastElement;
			
			return lastElement;
		}
		
		return '';
	}

	static isToday(date: Date) {
		const today = new Date();

		return date.getDate() == today.getDate() &&
			date.getMonth() == today.getMonth() &&
			date.getFullYear() == today.getFullYear();
	}

	static isCurrentYear(date: Date) {
		return date.getFullYear() == new Date().getFullYear();
	}

	static getDate(dateString?: string) {
		if (!dateString)
			return null;

		return new Date(dateString);
	}

	static formatDateString(date: string | Date) {
		if (typeof date == 'string')
			date = new Date(date);

		return date.toLocaleDateString('en-US', {
			month: '2-digit',
			day: '2-digit',
			year: 'numeric',
			hourCycle: 'h23',
			hour: 'numeric',
			minute: 'numeric'
		} as any).replace(',', '');
	}

	static fullMonthMeridiem(timestamp: number) {
		const date = new Date(timestamp),
			dateString = date.toDateString().substring(4);

		return `${dateString.slice(0, 6)}, ${dateString.slice(7)} ${date.toLocaleTimeString()}`;
	}

	static getFileNameTimestamp() {
		const date = new Date();

		return date.toLocaleString('en-US', {
			month: '2-digit',
			day: '2-digit',
			year: 'numeric'
		}).replace(/\//g, '_')
			
		+ ' ' +
		
		date.toLocaleString('en-US', {
			hourCycle: 'h23',
			hour: 'numeric',
			minute: 'numeric',
			second: 'numeric'
		} as any).replace(/:/g, '_');
	}

	static getTime(dateString: string) {
		return new Date(dateString).getTime();
	}

	static deepCopy(obj: any): any {
		let copy;

		if (obj == null || typeof obj != 'object')
			return obj;

		if (obj instanceof Date) {
			copy = new Date();
			copy.setTime(obj.getTime());
			
			return copy;
		}

		if (obj instanceof Array) {
			copy = [];
			for (let i = 0, len = obj.length; i < len; i++)
				copy[i] = Utils.deepCopy(obj[i]);
			
			return copy;
		}

		if (obj instanceof Object) {
			copy = {} as any;
			for (const attr in obj) {
				if (obj.hasOwnProperty(attr))
					copy[attr] = Utils.deepCopy(obj[attr]);
			}

			return copy;
		}

		throw new Error("Unable to copy obj! Its type isn't supported.");
	}
	
	static createSortFunction<E extends (object | string | number | boolean)>(byValue: SortKeyGetter<E>, type: SortOrder = 'asc') {
		const getCmpVal = (val: E) => typeof byValue === 'function' ? byValue(val) : val[byValue];
		return (a: E, b: E) => {
			const mod = type === 'desc' ? -1 : 1,
				val1 = getCmpVal(a),
				val2 = getCmpVal(b);
			
			let sort = 0;
			
			if (val1 < val2) {
				sort = -1;
			}
			else if (val1 > val2) {
				sort = 1;
			}
			return sort * mod;
		}
	}
	
	static createMultiSortFunction<E extends (object | string | number | boolean)>(byValues: Array<MultiSortOrder<E>> | Array<MultiSortConfig<E>>) {
		return (a: E, b: E) => {
			let sort = 0;
			for (let i = 0; i < byValues.length; i++) {
				
				const config = byValues[i];
				let sortFn: (a: E, b: E) => number;
				
				if (Array.isArray(config)) {
					sortFn = this.createSortFunction(config[0], config[1]);
				}
				else {
					if ('key' in config) {
						sortFn = this.createSortFunction(config.key, config.sort);
					}
					else {
						sortFn = config.compare;
					}
				}
				
				sort = sortFn(a, b);
				if (sort !== 0) break;
			}
			return sort;
		}
	}
	
	static arrayToMap<E>(array: E[], keyValue: keyof E): Record<string, E>
	static arrayToMap<E, R>(array: E[], keyValue: (obj: E, currentIndex: number) => [string | number, R]): Record<string, R>
	static arrayToMap<E>(array: E[], keyValue: (obj: E, currentIndex: number) => string | number): Record<string, E>
	static arrayToMap<E, R>(array: E[], keyValue: keyof E | ((obj: E, currentIndex: number) => string | number | [string | number, R])): Record<string, E> | Record<string, R> {
		return array.reduce((map, val, currentIndex) => {
			let key: string | [string, R];
			if (typeof keyValue === "function") {
				key = keyValue(val, currentIndex) as string | [string, R];
			}
			else {
				key = val[keyValue as keyof E] as unknown as any;
			}
			if (Array.isArray(key)) {
				map[key[0]] = key[1];
			}
			else {
				map[key] = val;
			}
			return map;
		}, {} as Record<string, E> | Record<string, R>)
	}
	
	static collectToMap<E, T>(array: E[], keyValue: (obj: E) => [string | number, T]): Record<string | number, Array<T>>
	static collectToMap<E>(array: E[], keyValue: (obj: E) => string | number): Record<string | number, Array<E>>
	static collectToMap<E, T>(array: E[], keyValue: (obj: E) => string | number | [(string | number), T]): Record<string | number, Array<E>> | Record<string | number, Array<T>> {
		return array.reduce((map, val) => {
			let kValue = keyValue(val);
			let key: string | number;
			let value: E | T;
			
			if (Array.isArray(kValue)) {
				key = kValue[0];
				value = kValue[1];
			}
			else {
				key = kValue;
				value = val;
			}
			
			const arr = map[key] = (map[key] ?? []);
			(arr as Array<T | E>).push(value);
			return map;
		}, {} as Record<string | number, Array<E>> | Record<string | number, Array<T>>)
	}
	
	static collectToEntries<T extends object, K extends PropertyKey>(array: T[], keyValue: (obj: T) => K): EntryCollection<T, K> {
		return array.reduce((collection, val, ) => {
			const key = keyValue(val);
			let entry = collection.find(([k]) => k === key) ?? Utils.also(([key, []] as EntryCollection<T, K>[number]), e => collection.push(e));
			entry[1].push(val);
			
			return collection;
		}, [] as EntryCollection<T, K>)
	}

	static hasShallowChanges(o1: any, o2: any): boolean {
		const
			formArr: (<T>(x: T) => T[]) = <T>(x: T) => Array.isArray(x) ? x : [x],
        	getDateISOStr = (d: any) => (d instanceof Date) ? d.toISOString() : d,
        	hasDiff = (o1: any, o2: any) => Object.keys(o1).reduce((changed, key) => {
				return changed || getDateISOStr(o1[key]) != getDateISOStr(o2[key]);
			}, false);

        return Object.keys(o1).reduce((changed, key) => {
			if (changed)
				return true;
			
			const a1 = formArr(o1[key]), a2 = formArr(o2[key])
			
            return (a1.length !== a2.length) || a1.reduce((changed, value, idx) => {
				const value2 = a2[idx];
				
                return (changed || typeof value !== typeof value2 || hasDiff(value, a2[idx]))
            }, false);
        }, false);
    }

    static isSafeNumber(number: number) {
		return this.isBetween(number, -2147483648, 2147483647);
	}

	static rgbToHex(rgb: string) {
		return `#${rgb.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/)
			?.slice(1)
			.map(n => parseInt(n, 10).toString(16).padStart(2, '0'))
			.join('')}`;
	}

	static numberToLowerAlpha(number: number) {
		const ordA = 'a'.charCodeAt(0),
			ordZ = 'z'.charCodeAt(0),
			len = ordZ - ordA + 1;
      
		let alpha = '';
		
		while (number >= 0) {
			alpha = String.fromCharCode(number % len + ordA) + alpha;
			number = Math.floor(number / len) - 1;
		}
		
		return alpha;
	}
	
	static numberToRomanNumeral(number: number) {
		const map = {
			m: 1000,
			cm: 900,
			d: 500,
			cd: 400,
			c: 100,
			xc: 90,
			l: 50,
			xl: 40,
			x: 10,
			ix: 9,
			v: 5,
			iv: 4,
			i: 1
		};

		let result = '';

		for (const [key, value] of Object.entries(map)) {
			result += key.repeat(Math.floor(number / value));
			number %= value;
		}
		  
		return result;
	}
	
	static isNotNullOrUndefined<T>(value: T | null | undefined): value is T {
		return value !== null && value !== undefined;
	}
	
	static isBooleanPredicate(obj: unknown): obj is boolean {
		return typeof obj === 'boolean';
	}
	
	static isStringPredicate(obj: unknown): obj is string {
		return typeof obj === 'string';
	}
	
	static isNumberPredicate(obj: unknown): obj is number {
		return typeof obj === 'number';
	}

	static downloadURI(uri: string, name = '') {
		const link = document.createElement('a');
		link.setAttribute('download', name);
		link.href = uri;
		document.body.appendChild(link);
		link.click();
		link.remove();
	}
	
	static generateUniqueId(): string {
		function r() {
			return Math.floor((1 + Math.random()) * 0x10000).toString(16).substring(1);
		}
		return r() + r() + '-' + r() + '-' + r() + '-' + r() + '-' + r() + r();
	}
	
	static convertRemToPixels(rem: number) {
		return rem * parseFloat(getComputedStyle(document.documentElement).fontSize);
	}
	
	static defineGetters<T, M extends TypedGetterMap>(obj: T, map: M): T & Readonly<{[K in keyof M]: ReturnType<M[K]['get']>}> {
		Object.entries(map).forEach((entry) => {
			Object.defineProperty(obj, entry[0], entry[1]);
		});
		
		return obj as any;
	}
	
	static defineGetterSetters<T, M extends TypedGetterSetterMap>(obj: T, map: M): T & {[K in keyof M]: ReturnType<M[K]['get']>} {
		Object.entries(map).forEach((entry) => {
			Object.defineProperty(obj, entry[0], entry[1]);
		});
		
		return obj as any;
	}
	
	/**
	 * Creates a proxy for the given object that tracks and notifies changes to its properties. The callback
	 * is not called if the new value is the same as the old value.
	 *
	 * @param obj The object to wrap in a change-tracking proxy.
	 * @param onChange A callback function triggered whenever a property is added, modified, or deleted.
	 * The function receives the property key, the new value, the old value, and the original object.
	 * @return The proxy object that tracks changes to the provided object.
	 */
	static createChangeTrackingProxy<T extends object>(obj: T, onChange: (key: keyof T, newValue: unknown, oldValue: unknown, originalObj: T) => void): T {
		const handler: ProxyHandler<T> = {
			set(target: T, p: string | symbol, value: any, receiver: any): boolean {
				const key = p as keyof T;
				const oldValue = target[key];
				const success = Reflect.set(target, p, value, receiver);
				if (success && oldValue != value) {
					onChange(key, value, oldValue, obj);
				}
				
				return success;
			},
			deleteProperty(target: T, p: string | symbol): boolean {
				const key = p as keyof T;
				const oldValue = target[key];
				const success = Reflect.deleteProperty(target, p);
				if (success && oldValue != undefined) {
					onChange(key, undefined, oldValue, obj);
				}
				
				return success;
			},
			defineProperty(target: T, p: string | symbol, attributes: PropertyDescriptor): boolean {
				const key = p as keyof T;
				const oldValue = target[key];
				const success = Reflect.defineProperty(target, p, attributes);
				if (success && oldValue != attributes.value) {
					onChange(key, attributes.value, oldValue, obj);
				}
				return success;
			}
		}

		return new Proxy(obj, handler);
	}
	
	static isValidNumber(o: any): o is number {
		return typeof o === 'number' && !isNaN(o);
	}
	
	static parseNumberOrUndefined(o: any, type: 'int' | 'float' = 'int'): number | undefined {
		if (o == null) {
			return undefined;
		}
		
		if (typeof o === 'number' && isNaN(o)) {
			return o;
		}
		
		const parsed = type === 'int' ? parseInt(o) : parseFloat(o);
		if (!isNaN(parsed) || parsed != null) {
			return parsed;
		}
		
		return undefined;
	}

	static let<T, R>(val: T, fn: (val: T) => R): R;
	static let<T>(val: T, fn: (val: T) => void): void;
	static let<T, R>(val: T, fn: (val: T) => R | void): R | void {
		return fn(val);
	}
	
	static also<T>(val: T, fn: (val: T) => void) {
		fn(val);
		return val;
	}
	
	static guard<T>(val: Nullish<T>, fn: (val: NonNullable<T>) => void) {
		if (val == null) return;
		fn(val!);
	}
	
	static guardLet<T, R>(val: Nullish<T>, fn: (val: NonNullable<T>) => R) {
		if (val == null) return;
		return this.let(val!, fn);
	}
	
	static clamp(value: number, min: number, max: number) {
		if (value < min) {
			return min;
		}
		if (value > max) {
			return max;
		}
		return value;
	}
	
	static trimToNull(input: string): string | null {
		const trimmed = input.trim();
		return trimmed.length > 0 ? trimmed : null;
	}
	
	/**
	 * Calculates the bounding rectangle of a specified element in relation to the entire window, taking into account scroll offsets.
	 *
	 * @param {Element} element - The DOM element for which to retrieve the rectangle in relation to the window.
	 * @return {DOMRect} The rectangle object representing the element's position and dimensions relative to the entire window.
	 */
	static getElementRectInRelationToWindow(element: Element): DOMRect {
		const rect = element.getBoundingClientRect();
		
		const scrollTop = window.scrollY || document.documentElement.scrollTop;
		
		const scrollLeft = window.scrollX || document.documentElement.scrollLeft;
		
		// Rect position in relation to viewport
		return new DOMRect(
			rect.left + scrollLeft,
			rect.top + scrollTop,
			rect.width,
			rect.height
		);
	}
	
	static isElementOutsideOfViewport(element: Element): boolean {
		const offsetRect = this.getElementRectInRelationToWindow(element);
		
		const scrollTop = window.scrollY || document.documentElement.scrollTop;
		const viewHeight = window.innerHeight || document.documentElement.clientHeight;
		const scrollBottom = scrollTop + viewHeight;
		
		const scrollLeft = window.scrollX || document.documentElement.scrollLeft;
		const viewWidth = window.innerWidth || document.documentElement.clientWidth;
		const scrollRight = scrollLeft + viewWidth;
		
		return (
			offsetRect.top < scrollTop ||
			offsetRect.bottom > scrollBottom ||
			offsetRect.left < scrollLeft ||
			offsetRect.right > scrollRight
		);
	}
	
	/**
	 * Creates a closure function by executing the provided function.
	 * @param setup Function that provides the closure.
	 * @param thisType Type of the `this` context to bind to the closure function.
	 */
	static closure<FnType extends Function, ThisType>(setup: () => FnType, thisType?: ThisType): FnType {
		const res = setup();
		
		if (thisType != null) {
			return res.bind(thisType);
		}
		return res;
	}
}